/**
 * DXF の読み込み（docs/dxf.md 2 章）。dxf-parser で読み、図形を折れ線にそろえる。
 * ブラウザ・React Native・Workers のどこでも動く（Node 専用の API を使わない）。
 */

import type { Point } from "@el/schema";
import DxfParser from "dxf-parser";
import type { Rect } from "../geometry";

export type DxfRole = "wall" | "column" | "door" | "existing" | "zone" | "ignore";
export type DxfLayer = { name: string; entityCount: number; suggestedRole: DxfRole };

/** 図形（図面の単位、DXF の座標＝y 上向き）。円弧・円は線分に分けてある */
export type DxfShape = {
  layer: string;
  points: Point[];
  closed: boolean;
  /** ブロック参照（INSERT）から展開したものは、その参照ごとの番号。単独の図形は -1 */
  group: number;
  type: string;
};
export type DxfText = { layer: string; text: string; at: Point };

export type DxfDocument = {
  /** 図面の 1 単位が何 mm か */
  unitsMm: number;
  /** $INSUNITS が無い・不明で、大きさから推定した */
  unitGuessed: boolean;
  /** ヘッダーの $INSUNITS（無ければ null） */
  insUnits: number | null;
  layers: DxfLayer[];
  /** 図形全体の範囲（mm、y を下向きに反転した座標。移動はしない） */
  bounds: Rect;
  shapes: DxfShape[];
  texts: DxfText[];
};

export type DxfErrorCode = "empty" | "broken" | "encoding";

/** 利用者に見せる文（何が起きたかと次にすること） */
export const DXF_ERROR_MESSAGES: Record<DxfErrorCode, string> = {
  empty: "図面に読み込める図形がありませんでした。外壁や柱が描かれた DXF ファイルを選んでください。",
  broken: "図面のファイルが壊れているか途中で切れているため、読み込めませんでした。CAD で DXF（R2010 以降）として保存し直してください。",
  encoding: "この端末ではファイルの文字コードを読めませんでした。CAD で DXF（R2010 以降）として保存し直してください。",
};

export class DxfError extends Error {
  readonly code: DxfErrorCode;
  /** 原因の詳しい内容（ライブラリの英語の文など。画面には出さず、記録用） */
  readonly detail: string | undefined;
  constructor(code: DxfErrorCode, detail?: string) {
    super(DXF_ERROR_MESSAGES[code]);
    this.name = "DxfError";
    this.code = code;
    this.detail = detail;
  }
}

/** $INSUNITS → mm */
const INSUNITS_MM: Record<number, number> = {
  1: 25.4, // inch
  2: 304.8, // feet
  4: 1, // mm
  5: 10, // cm
  6: 1000, // m
  7: 1_000_000, // km
  8: 0.0000254, // microinch
  9: 0.0254, // mil
  10: 914.4, // yard
  13: 0.001, // micron
  14: 100, // decimeter
  15: 10_000, // decameter
  16: 100_000, // hectometer
};

export const UNIT_NAMES: Record<number, string> = { 1: "mm", 10: "cm", 1000: "m", 25.4: "inch", 304.8: "feet" };

/** 円弧を線分に分けるときの弦の誤差（mm） */
const ARC_TOLERANCE_MM = 5;
const MAX_BLOCK_DEPTH = 16;
const OCS_TYPES = new Set(["ARC", "CIRCLE", "LWPOLYLINE", "POLYLINE", "INSERT", "SOLID", "TEXT", "MTEXT"]);

/** "\U+30C6" のような書き方の文字を戻す */
export function unescapeDxfText(s: string): string {
  return s.replace(/\\U\+([0-9A-Fa-f]{4})/g, (_, h: string) => String.fromCharCode(parseInt(h, 16)));
}

/* ===== 文字コード ===== */

/**
 * ファイルのバイト列を文字列にする。UTF-8 として読めなければ Shift_JIS（$DWGCODEPAGE が ANSI_932 の古い DXF）として読む。
 * Shift_JIS を読めない環境では DxfError("encoding") を投げる。
 */
export function decodeDxfBytes(bytes: Uint8Array): string {
  let b = bytes;
  if (b.length >= 3 && b[0] === 0xef && b[1] === 0xbb && b[2] === 0xbf) b = b.subarray(3);
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(b);
  } catch {
    // UTF-8 ではない
  }
  let ascii = "";
  const head = b.subarray(0, Math.min(b.length, 200_000));
  for (let i = 0; i < head.length; i++) ascii += String.fromCharCode(head[i]! < 0x80 ? head[i]! : 0x3f);
  const cp = /\$DWGCODEPAGE\s*\r?\n\s*3\s*\r?\n\s*([A-Za-z0-9_]+)/.exec(ascii)?.[1]?.toUpperCase();
  const label = cp === "ANSI_1252" ? "windows-1252" : cp === "ANSI_936" ? "gbk" : cp === "ANSI_949" ? "euc-kr" : "shift_jis";
  try {
    return new TextDecoder(label).decode(b);
  } catch {
    throw new DxfError("encoding", `TextDecoder("${label}") is not supported`);
  }
}

/* ===== 座標変換 ===== */

type Affine = { a: number; b: number; c: number; d: number; e: number; f: number };
const IDENTITY: Affine = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

function apply(m: Affine, x: number, y: number): Point {
  return { x: m.a * x + m.c * y + m.e, y: m.b * x + m.d * y + m.f };
}

function mul(m: Affine, n: Affine): Affine {
  return {
    a: m.a * n.a + m.c * n.b,
    b: m.b * n.a + m.d * n.b,
    c: m.a * n.c + m.c * n.d,
    d: m.b * n.c + m.d * n.d,
    e: m.a * n.e + m.c * n.f + m.e,
    f: m.b * n.e + m.d * n.f + m.f,
  };
}

function scaleOf(m: Affine): number {
  return Math.max(Math.hypot(m.a, m.b), Math.hypot(m.c, m.d));
}

function num(v: unknown, fallback = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/* ===== 図形の展開 ===== */

type RawEntity = Record<string, unknown> & { type?: string; layer?: string };
type RawBlock = { entities?: RawEntity[]; position?: { x?: number; y?: number } };
type RawDoc = {
  header?: Record<string, unknown>;
  tables?: { layer?: { layers?: Record<string, { name?: string }> } };
  blocks?: Record<string, RawBlock>;
  entities?: RawEntity[];
};

type Ctx = {
  blocks: Record<string, RawBlock>;
  /** 図面単位 1 の mm（円弧の分割数に使う。分からなければ 0 で固定数） */
  unitMm: number;
  shapes: DxfShape[];
  texts: DxfText[];
};

function arcSegments(radius: number, sweep: number, m: Affine, unitMm: number): number {
  const rmm = Math.abs(radius) * scaleOf(m) * (unitMm || 1);
  if (unitMm === 0) return Math.max(4, Math.ceil((Math.abs(sweep) / (2 * Math.PI)) * 32));
  if (rmm <= ARC_TOLERANCE_MM) return Math.max(2, Math.ceil((Math.abs(sweep) / (2 * Math.PI)) * 8));
  const step = 2 * Math.acos(1 - ARC_TOLERANCE_MM / rmm);
  return Math.min(1024, Math.max(2, Math.ceil(Math.abs(sweep) / step)));
}

function arcPoints(cx: number, cy: number, r: number, a0: number, a1: number, m: Affine, unitMm: number, includeEnd = true): Point[] {
  const sweep = a1 - a0;
  const n = arcSegments(r, sweep, m, unitMm);
  const pts: Point[] = [];
  const last = includeEnd ? n : n - 1;
  for (let i = 0; i <= last; i++) {
    const t = a0 + (sweep * i) / n;
    pts.push(apply(m, cx + r * Math.cos(t), cy + r * Math.sin(t)));
  }
  return pts;
}

/** ふくらみ（bulge）付きの頂点列を線分に */
function bulgePolyline(vs: { x?: number; y?: number; bulge?: number }[], closed: boolean, m: Affine, unitMm: number): Point[] {
  const pts: Point[] = [];
  const n = vs.length;
  const segs = closed ? n : n - 1;
  for (let i = 0; i < n; i++) {
    const p = vs[i]!;
    const x0 = num(p.x);
    const y0 = num(p.y);
    pts.push(apply(m, x0, y0));
    if (i >= segs) continue;
    const bulge = num(p.bulge);
    if (bulge === 0) continue;
    const q = vs[(i + 1) % n]!;
    const x1 = num(q.x);
    const y1 = num(q.y);
    const chord = Math.hypot(x1 - x0, y1 - y0);
    if (chord === 0) continue;
    const theta = 4 * Math.atan(bulge);
    const r = chord / (2 * Math.sin(Math.abs(theta) / 2));
    const mx = (x0 + x1) / 2;
    const my = (y0 + y1) / 2;
    // 弦の中点から中心までの距離（符号は向きで決める）
    const h = Math.sqrt(Math.max(0, r * r - (chord / 2) ** 2));
    const nx = -(y1 - y0) / chord;
    const ny = (x1 - x0) / chord;
    const sign = (bulge > 0 ? 1 : -1) * (Math.abs(theta) > Math.PI ? -1 : 1);
    const cx = mx + nx * h * sign;
    const cy = my + ny * h * sign;
    const a0 = Math.atan2(y0 - cy, x0 - cx);
    const inner = arcPoints(cx, cy, r, a0, a0 + theta, m, unitMm);
    pts.push(...inner.slice(1, -1));
  }
  return pts;
}

function addShape(ctx: Ctx, layer: string, points: Point[], closed: boolean, group: number, type: string) {
  const pts = points.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (pts.length < 2 && !(pts.length === 1 && type === "POINT")) return;
  ctx.shapes.push({ layer, points: pts, closed, group, type });
}

function emit(ctx: Ctx, e: RawEntity, m: Affine, parentLayer: string | null, group: number, depth: number) {
  const ownLayer = typeof e.layer === "string" && e.layer.length > 0 ? unescapeDxfText(e.layer) : "0";
  const layer = parentLayer !== null && ownLayer === "0" ? parentLayer : ownLayer;
  const type = String(e.type ?? "");
  // 鏡写しで作った図形（押し出し方向が -Z）は、OCS の x が反転している
  const ez = num(e.extrusionDirectionZ, num((e.extrusionDirection as { z?: number } | undefined)?.z, 1));
  if (ez < 0 && OCS_TYPES.has(type)) m = mul(m, { a: -1, b: 0, c: 0, d: 1, e: 0, f: 0 });
  switch (type) {
    case "LINE": {
      const vs = (e.vertices as { x?: number; y?: number }[] | undefined) ?? [];
      if (vs.length >= 2) addShape(ctx, layer, [apply(m, num(vs[0]!.x), num(vs[0]!.y)), apply(m, num(vs[1]!.x), num(vs[1]!.y))], false, group, type);
      return;
    }
    case "LWPOLYLINE":
    case "POLYLINE": {
      if (e.isPolyfaceMesh || e.is3dPolygonMesh) return;
      const vs = ((e.vertices as { x?: number; y?: number; bulge?: number }[] | undefined) ?? []).filter((v) => typeof v.x === "number" && typeof v.y === "number");
      if (vs.length < 2) return;
      const closed = Boolean(e.shape);
      addShape(ctx, layer, bulgePolyline(vs, closed, m, ctx.unitMm), closed, group, type);
      return;
    }
    case "ARC": {
      const c = (e.center as { x?: number; y?: number }) ?? {};
      const r = num(e.radius);
      if (r <= 0) return;
      const a0 = num(e.startAngle);
      let a1 = num(e.endAngle);
      while (a1 <= a0) a1 += 2 * Math.PI;
      addShape(ctx, layer, arcPoints(num(c.x), num(c.y), r, a0, a1, m, ctx.unitMm), false, group, type);
      return;
    }
    case "CIRCLE": {
      const c = (e.center as { x?: number; y?: number }) ?? {};
      const r = num(e.radius);
      if (r <= 0) return;
      addShape(ctx, layer, arcPoints(num(c.x), num(c.y), r, 0, 2 * Math.PI, m, ctx.unitMm, false), true, group, type);
      return;
    }
    case "ELLIPSE": {
      const c = (e.center as { x?: number; y?: number }) ?? {};
      const mj = (e.majorAxisEndPoint as { x?: number; y?: number }) ?? {};
      const ratio = num(e.axisRatio, 1);
      const mx = num(mj.x);
      const my = num(mj.y);
      const major = Math.hypot(mx, my);
      if (major === 0) return;
      const t0 = num(e.startAngle, 0);
      let t1 = num(e.endAngle, 2 * Math.PI);
      while (t1 <= t0) t1 += 2 * Math.PI;
      const full = Math.abs(t1 - t0 - 2 * Math.PI) < 1e-9;
      const n = arcSegments(major, t1 - t0, m, ctx.unitMm);
      const pts: Point[] = [];
      const last = full ? n - 1 : n;
      for (let i = 0; i <= last; i++) {
        const t = t0 + ((t1 - t0) * i) / n;
        const lx = mx * Math.cos(t) - my * ratio * Math.sin(t);
        const ly = my * Math.cos(t) + mx * ratio * Math.sin(t);
        pts.push(apply(m, num(c.x) + lx, num(c.y) + ly));
      }
      addShape(ctx, layer, pts, full, group, type);
      return;
    }
    case "SPLINE": {
      const vs = (e.fitPoints as { x: number; y: number }[] | undefined) ?? (e.controlPoints as { x: number; y: number }[] | undefined) ?? [];
      if (vs.length < 2) return;
      addShape(
        ctx,
        layer,
        vs.map((v) => apply(m, num(v.x), num(v.y))),
        Boolean(e.closed),
        group,
        type,
      );
      return;
    }
    case "SOLID": {
      const ps = ((e.points as { x: number; y: number }[] | undefined) ?? []).filter(Boolean);
      if (ps.length < 3) return;
      // SOLID の頂点の順は 1-2-4-3
      const order = ps.length === 4 ? [ps[0]!, ps[1]!, ps[3]!, ps[2]!] : ps;
      addShape(
        ctx,
        layer,
        order.map((v) => apply(m, num(v.x), num(v.y))),
        true,
        group,
        type,
      );
      return;
    }
    case "TEXT":
    case "MTEXT": {
      const at = ((e.startPoint ?? e.position) as { x?: number; y?: number }) ?? {};
      const text = typeof e.text === "string" ? unescapeDxfText(e.text) : "";
      if (text) ctx.texts.push({ layer, text, at: apply(m, num(at.x), num(at.y)) });
      return;
    }
    case "INSERT": {
      if (depth >= MAX_BLOCK_DEPTH) return;
      const name = String(e.name ?? "");
      const block = ctx.blocks[name];
      if (!block?.entities) return;
      const pos = (e.position as { x?: number; y?: number }) ?? {};
      const sx = num(e.xScale, 1) || 1;
      const sy = num(e.yScale, 1) || 1;
      const rot = (num(e.rotation) * Math.PI) / 180;
      const cos = Math.cos(rot);
      const sin = Math.sin(rot);
      const base = block.position ?? {};
      const cols = Math.max(1, Math.min(100, Math.floor(num(e.columnCount, 1))));
      const rows = Math.max(1, Math.min(100, Math.floor(num(e.rowCount, 1))));
      const cs = num(e.columnSpacing);
      const rs = num(e.rowSpacing);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const ox = c * cs;
          const oy = r * rs;
          // 位置へ移動 · 回転 · 配列のずれ · 拡大 · 基点を原点へ
          const local: Affine = {
            a: cos * sx,
            b: sin * sx,
            c: -sin * sy,
            d: cos * sy,
            e: num(pos.x) + cos * ox - sin * oy - (cos * sx * num(base.x) - sin * sy * num(base.y)),
            f: num(pos.y) + sin * ox + cos * oy - (sin * sx * num(base.x) + cos * sy * num(base.y)),
          };
          const mm = mul(m, local);
          for (const child of block.entities) emit(ctx, child, mm, layer, group, depth + 1);
        }
      }
      return;
    }
    default:
      return;
  }
}

function expand(raw: RawDoc, unitMm: number): Ctx {
  const ctx: Ctx = { blocks: raw.blocks ?? {}, unitMm, shapes: [], texts: [] };
  let group = 0;
  for (const e of raw.entities ?? []) {
    if ((e as { inPaperSpace?: boolean }).inPaperSpace) continue;
    emit(ctx, e, IDENTITY, null, e.type === "INSERT" ? group++ : -1, 0);
  }
  return ctx;
}

function rawBox(shapes: DxfShape[]): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const s of shapes) {
    for (const p of s.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null;
}

/**
 * 単位の推定。図面全体の大きさ E（図面の単位）から、建屋として自然な単位を選ぶ。
 * E < 200 → m、200 ≦ E < 2,000 → cm、それ以上 → mm。
 */
export function guessUnitsMm(extent: number): number {
  if (!(extent > 0)) return 1;
  if (extent < 200) return 1000;
  if (extent < 2000) return 10;
  return 1;
}

/* ===== レイヤーの役割の推定 ===== */

// AISLE だけの名前は書き出した図の「作業者側の通路の帯」なので読まない。それ以外の通路・置き場は zone
const RE_IGNORE = /DIM|寸法|TEXT|文字|注記|HATCH|ハッチ|DEFPOINTS|芯|CENTER|GRID|AXIS|TITLE|図枠|CLEARANCE|CONVEYOR|^AISLE$|搬送/i;
const RE_ZONE = /ZONE|ゾーン|通路|AISLE|WALKWAY|PASSAGE|置き場|置場|仮置|立入|保全|フォーク|FORK/i;
const RE_WALL = /WALL|壁|躯体|外形|外周|建屋|BUILDING|OUTLINE|SPACE|KABE/i;
const RE_COLUMN = /^COL|COLUMN|柱|HASHIRA|PILLAR/i;
const RE_DOOR = /DOOR|扉|ドア|出入口|入口|シャッター|SHUTTER|GATE|開口/i;
const RE_EXISTING = /EQUIP|既存|設備|MACHINE|機械/i;
const RE_OBSTACLE = /OBSTACLE|障害/i;

/** 柱とみなす閉じた図形の大きさの上限（mm） */
const COLUMN_MAX_MM = 1500;

function shapeExtent(s: DxfShape): { w: number; h: number } {
  const b = rawBox([s])!;
  return { w: b.maxX - b.minX, h: b.maxY - b.minY };
}

function guessRole(name: string, shapes: DxfShape[], textOnly: boolean, drawing: { w: number; h: number }, unitMm: number): DxfRole {
  if (RE_IGNORE.test(name)) return "ignore";
  if (RE_WALL.test(name)) return "wall";
  if (RE_COLUMN.test(name)) return "column";
  if (RE_DOOR.test(name)) return "door";
  if (RE_ZONE.test(name)) return "zone";
  if (RE_EXISTING.test(name)) return "existing";
  if (shapes.length === 0 || textOnly) return "ignore";
  const closedSmall = shapes.filter((s) => {
    if (!s.closed) return false;
    const e = shapeExtent(s);
    return Math.max(e.w, e.h) * unitMm <= COLUMN_MAX_MM && Math.min(e.w, e.h) > 0;
  }).length;
  if (RE_OBSTACLE.test(name)) return closedSmall * 2 >= shapes.length ? "column" : "existing";
  const b = rawBox(shapes)!;
  if (drawing.w > 0 && drawing.h > 0 && b.maxX - b.minX >= drawing.w * 0.9 && b.maxY - b.minY >= drawing.h * 0.9) return "wall";
  if (closedSmall > 0 && closedSmall * 10 >= shapes.length * 6) return "column";
  return "ignore";
}

/* ===== 入口 ===== */

export function parseDxf(text: string): DxfDocument {
  if (typeof text !== "string" || text.trim().length === 0) throw new DxfError("empty", "empty file");
  let raw: RawDoc | null;
  try {
    raw = new DxfParser().parseSync(text) as unknown as RawDoc | null;
  } catch (err) {
    throw new DxfError("broken", err instanceof Error ? err.message : String(err));
  }
  if (!raw) throw new DxfError("broken", "parser returned nothing");
  if (!raw.entities && !raw.header && !raw.tables && !raw.blocks) {
    // 「0 / EOF」だけの正しいが空の DXF と、DXF ではない文字を分ける
    if (/(^|\n)\s*0\s*\r?\n\s*EOF\s*$/.test(text)) throw new DxfError("empty", "only EOF");
    throw new DxfError("broken", "no DXF sections found");
  }

  const ins = raw.header?.$INSUNITS;
  const insUnits = typeof ins === "number" && Number.isFinite(ins) ? ins : null;
  let unitsMm = insUnits !== null ? INSUNITS_MM[insUnits] : undefined;
  const unitGuessed = unitsMm === undefined;

  let ctx: Ctx;
  if (unitsMm === undefined) {
    const rough = expand(raw, 0);
    const b = rawBox(rough.shapes);
    unitsMm = guessUnitsMm(b ? Math.max(b.maxX - b.minX, b.maxY - b.minY) : 0);
  }
  ctx = expand(raw, unitsMm);
  if (ctx.shapes.length === 0 && ctx.texts.length === 0) throw new DxfError("empty", "no entities");

  // レイヤー
  const order: string[] = [];
  const seen = new Set<string>();
  const addLayer = (n: string) => {
    if (!seen.has(n)) {
      seen.add(n);
      order.push(n);
    }
  };
  for (const [key, l] of Object.entries(raw.tables?.layer?.layers ?? {})) addLayer(unescapeDxfText(l?.name ?? key));
  const counts = new Map<string, number>();
  for (const e of raw.entities ?? []) {
    const n = typeof e.layer === "string" && e.layer ? unescapeDxfText(e.layer) : "0";
    addLayer(n);
    counts.set(n, (counts.get(n) ?? 0) + 1);
  }
  for (const s of ctx.shapes) addLayer(s.layer);
  const byLayer = new Map<string, DxfShape[]>();
  for (const s of ctx.shapes) {
    const arr = byLayer.get(s.layer) ?? [];
    arr.push(s);
    byLayer.set(s.layer, arr);
  }
  const all = rawBox(ctx.shapes);
  const drawing = all ? { w: all.maxX - all.minX, h: all.maxY - all.minY } : { w: 0, h: 0 };
  const textLayers = new Set(ctx.texts.map((t) => t.layer));
  const layers: DxfLayer[] = order.map((name) => {
    const shapes = byLayer.get(name) ?? [];
    return {
      name,
      entityCount: counts.get(name) ?? 0,
      suggestedRole: guessRole(name, shapes, shapes.length === 0 && textLayers.has(name), drawing, unitsMm!),
    };
  });

  const bounds: Rect = all ? { x: all.minX * unitsMm, y: -all.maxY * unitsMm, w: drawing.w * unitsMm, h: drawing.h * unitsMm } : { x: 0, y: 0, w: 0, h: 0 };
  return { unitsMm, unitGuessed, insUnits, layers, bounds, shapes: ctx.shapes, texts: ctx.texts };
}
